import type { CreateWorkspaceInput, OrgxWorkspace, SetDefaultWorkspaceInput } from "./workspaces.js";

const CREATE_WORKSPACE_VALUE = "__create_workspace__";
const SKIP_WORKSPACE_VALUE = "__skip_workspace__";
const REAUTH_WORKSPACE_VALUE = "__reauth_workspace__";

export interface WorkspaceSetupClient {
  createWorkspace(input: CreateWorkspaceInput): Promise<OrgxWorkspace>;
  getCurrentWorkspace(): Promise<OrgxWorkspace | null>;
  listWorkspaces(): Promise<OrgxWorkspace[]>;
  setDefaultWorkspace(
    input: SetDefaultWorkspaceInput,
  ): Promise<{ changed: boolean; workspace: OrgxWorkspace }>;
}

export interface WorkspaceSelectOption {
  hint?: string;
  label: string;
  value: string;
}

export interface WorkspaceSetupPrompts {
  cancel(message: string): void;
  isCancel(value: unknown): boolean;
  select(input: {
    initialValue?: string | undefined;
    message: string;
    options: WorkspaceSelectOption[];
  }): Promise<string | symbol>;
  text(input: {
    initialValue?: string | undefined;
    message: string;
    placeholder?: string;
    validate?: (value: string | undefined) => Error | string | undefined;
  }): Promise<string | symbol>;
}

export interface WorkspaceSetupOptions {
  interactive: boolean;
  skipIfConfigured?: boolean;
}

export interface WorkspaceSetupResult {
  created?: boolean;
  defaultChanged?: boolean;
  message: string;
  /**
   * "reauth_requested" — the caller should trigger the OAuth login flow and
   * then rerun setup. This is used when the key authenticates but returns no
   * workspaces — a strong signal the key is bound to a different Supabase
   * user than the one the human signs into via OAuth.
   */
  status: "cancelled" | "skipped" | "unchanged" | "updated" | "reauth_requested";
  workspace?: OrgxWorkspace;
}

function trimDescription(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function cancelResult(
  prompts: WorkspaceSetupPrompts,
  message = "Workspace bootstrap cancelled.",
): WorkspaceSetupResult {
  prompts.cancel(message);
  return {
    message,
    status: "cancelled",
  };
}

function buildWorkspaceLabel(workspace: OrgxWorkspace, currentWorkspaceId?: string): string {
  const suffixes: string[] = [];

  if (workspace.isDefault) {
    suffixes.push("default");
  }

  if (workspace.id === currentWorkspaceId) {
    suffixes.push("current");
  }

  if (suffixes.length === 0) {
    return workspace.name;
  }

  return `${workspace.name} (${suffixes.join(", ")})`;
}

export function buildWorkspaceSelectOptions(
  workspaces: OrgxWorkspace[],
  currentWorkspaceId?: string,
): WorkspaceSelectOption[] {
  const options = workspaces.map((workspace) => ({
    value: workspace.id,
    label: buildWorkspaceLabel(workspace, currentWorkspaceId),
    ...(workspace.description ? { hint: workspace.description } : {}),
  }));

  options.push(
    {
      value: CREATE_WORKSPACE_VALUE,
      label: "Create a new workspace",
      hint: "Name it here and set it as the default for this machine.",
    },
    {
      value: SKIP_WORKSPACE_VALUE,
      label: "Skip for now",
      hint: "Leave workspace selection unchanged and finish setup.",
    },
  );

  return options;
}

async function promptForWorkspaceName(prompts: WorkspaceSetupPrompts): Promise<string | symbol> {
  return prompts.text({
    message: "Workspace name",
    placeholder: "Founders",
    validate(value) {
      if (!value || value.trim().length === 0) {
        return "Workspace name is required.";
      }

      return undefined;
    },
  });
}

async function promptForWorkspaceDescription(
  prompts: WorkspaceSetupPrompts,
): Promise<string | symbol> {
  return prompts.text({
    message: "Workspace description",
    placeholder: "Optional",
  });
}

async function createAndSelectWorkspace(
  client: WorkspaceSetupClient,
  prompts: WorkspaceSetupPrompts,
): Promise<WorkspaceSetupResult> {
  const name = await promptForWorkspaceName(prompts);
  if (prompts.isCancel(name)) {
    return cancelResult(prompts);
  }
  if (typeof name !== "string") {
    return cancelResult(prompts);
  }

  const description = await promptForWorkspaceDescription(prompts);
  if (prompts.isCancel(description)) {
    return cancelResult(prompts);
  }
  if (typeof description !== "string") {
    return cancelResult(prompts);
  }

  const trimmedDescription = trimDescription(description);
  const createWorkspaceInput: CreateWorkspaceInput = trimmedDescription
    ? { name, description: trimmedDescription }
    : { name };

  const created = await client.createWorkspace(createWorkspaceInput);

  if (created.isDefault) {
    return {
      created: true,
      defaultChanged: false,
      message: `Created "${created.name}" and it is already the default OrgX workspace.`,
      status: "updated",
      workspace: created,
    };
  }

  const promoted = await client.setDefaultWorkspace({ id: created.id });
  return {
    created: true,
    defaultChanged: promoted.changed,
    message: `Created "${promoted.workspace.name}" and set it as the default OrgX workspace.`,
    status: "updated",
    workspace: promoted.workspace,
  };
}

export async function runWorkspaceSetup(
  client: WorkspaceSetupClient,
  prompts: WorkspaceSetupPrompts,
  options: WorkspaceSetupOptions,
): Promise<WorkspaceSetupResult> {
  if (!options.interactive) {
    return {
      message:
        "Interactive workspace bootstrap skipped because this shell is not attached to a TTY.",
      status: "skipped",
    };
  }

  const workspaces = await client.listWorkspaces();
  const currentWorkspace =
    workspaces.length > 0
      ? await client.getCurrentWorkspace().catch(() => workspaces.find((workspace) => workspace.isDefault) ?? null)
      : null;

  if (workspaces.length === 0) {
    // The API key authenticated successfully (we reached this function) but
    // the server returned zero workspaces for the resolved user. That is
    // ambiguous: it could mean a genuinely new account, OR the key is bound
    // to a different Supabase user row than the one that owns this person's
    // workspaces (e.g. the key was provisioned before the OAuth/Clerk sign-in
    // unified the user record). Creating a new workspace in that second case
    // silently forks the user's data — so surface the choice explicitly and
    // offer re-authentication as the first-class recovery path.
    const action = await prompts.select({
      message:
        "OrgX authenticated, but returned 0 workspaces for this key. How should setup proceed?",
      options: [
        {
          value: REAUTH_WORKSPACE_VALUE,
          label: "Re-authenticate (recommended if you expected workspaces)",
          hint: "Run `wizard auth login` to re-link via OAuth if your API key is bound to a different user identity.",
        },
        {
          value: CREATE_WORKSPACE_VALUE,
          label: "Create a new workspace",
          hint: "Use this only for a truly new account — this will fork data if your key is misbound.",
        },
        {
          value: SKIP_WORKSPACE_VALUE,
          label: "Skip for now",
          hint: "Finish surface setup without touching workspaces.",
        },
      ],
      initialValue: REAUTH_WORKSPACE_VALUE,
    });

    if (prompts.isCancel(action)) {
      return cancelResult(prompts);
    }

    if (action === REAUTH_WORKSPACE_VALUE) {
      return {
        message:
          "Re-authentication requested. Run `wizard auth login`, then rerun `wizard setup`.",
        status: "reauth_requested",
      };
    }

    if (action === SKIP_WORKSPACE_VALUE) {
      return {
        message: "Workspace bootstrap skipped.",
        status: "skipped",
      };
    }

    return createAndSelectWorkspace(client, prompts);
  }

  if (options.skipIfConfigured && currentWorkspace) {
    return {
      defaultChanged: false,
      message: `Using "${currentWorkspace.name}" as the default OrgX workspace.`,
      status: "unchanged",
      workspace: currentWorkspace,
    };
  }

  const initialWorkspaceId =
    currentWorkspace?.id ??
    workspaces.find((workspace) => workspace.isDefault)?.id ??
    workspaces[0]?.id;
  const selected = await prompts.select({
    message: "Choose the OrgX workspace this machine should use by default.",
    options: buildWorkspaceSelectOptions(
      workspaces,
      currentWorkspace?.id ?? workspaces.find((workspace) => workspace.isDefault)?.id,
    ),
    ...(initialWorkspaceId ? { initialValue: initialWorkspaceId } : {}),
  });

  if (prompts.isCancel(selected)) {
    return cancelResult(prompts);
  }
  if (typeof selected !== "string") {
    return cancelResult(prompts);
  }

  if (selected === SKIP_WORKSPACE_VALUE) {
    return {
      message: "Workspace bootstrap skipped.",
      status: "skipped",
      ...(currentWorkspace ? { workspace: currentWorkspace } : {}),
    };
  }

  if (selected === CREATE_WORKSPACE_VALUE) {
    return createAndSelectWorkspace(client, prompts);
  }

  const chosenWorkspace = workspaces.find((workspace) => workspace.id === selected);
  if (!chosenWorkspace) {
    throw new Error(`Selected workspace ${selected} is no longer available.`);
  }

  if (chosenWorkspace.isDefault) {
    return {
      defaultChanged: false,
      message: `"${chosenWorkspace.name}" is already the default OrgX workspace.`,
      status: "unchanged",
      workspace: chosenWorkspace,
    };
  }

  const promoted = await client.setDefaultWorkspace({ id: chosenWorkspace.id });
  return {
    defaultChanged: promoted.changed,
    message: `Set "${promoted.workspace.name}" as the default OrgX workspace.`,
    status: promoted.changed ? "updated" : "unchanged",
    workspace: promoted.workspace,
  };
}
